import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { formatFileNameProduct } from 'utils/func';
import SubAllocatedExpenditureTable from './SubAllocatedExpenditureTable';
import AllocatedExpenditureDialog from './AllocatedExpenditureDialog';

export default function AllocatedExpenditureTable({
  disabled,
  control,
  errors,
  setValue,
  expenditureFromOtherFund,
  projectCode,
  setExpenditurePeriodsDelete,
  handleDownloadFile,
}) {
  const { t } = useTranslation();
  const allocatedExpenditureDialogRef = useRef(null);

  return (
    <div className="form-table mb-4">
      <table>
        <thead>
          <tr>
            <th style={{ minWidth: 300, width: 460 }}>{t('r05.b.b4.expenditure')}</th>
            <th style={{ minWidth: 200, width: 200 }}>{t('r05.b.b4.amountOfMoney')}</th>
            <th style={{ minWidth: 220, width: 300 }}>{t('r05.a.a5.table.proof')}</th>
            {!disabled && (
              <th className="text-center float" style={{ minWidth: 60, width: 82 }}>
                {t('projectProposal.action')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={4}>
              <p className="font-bold m-0">{t('r05.a.a5.table.expenditureFromVNU')}</p>
              <SubAllocatedExpenditureTable
                control={control}
                errors={errors}
                disabled={disabled}
                projectCode={projectCode}
                setExpenditurePeriodsDelete={setExpenditurePeriodsDelete}
                handleDownloadFile={handleDownloadFile}
              />
            </td>
          </tr>
          <tr>
            <td>
              <p className="font-bold">{t('r05.a.a5.table.expenditureFromOtherFund')}</p>
            </td>
            <td className="text-right">
              {expenditureFromOtherFund?.amount?.toLocaleString('vi-VN', {
                style: 'currency',
                currency: 'VND',
              })}
            </td>
            <td>
              {!expenditureFromOtherFund?.file?.name ? (
                expenditureFromOtherFund?.filename && (
                  <span
                    className="p-button p-button-text p-button-sm mt-1 p-0"
                    onClick={() => {
                      handleDownloadFile(
                        expenditureFromOtherFund?.filename,
                        formatFileNameProduct(
                          expenditureFromOtherFund?.filename || '',
                          projectCode,
                          'KP',
                          -1
                        ),
                        'summary-report-allocated-expenditures'
                      );
                    }}
                  >
                    <i
                      className={`pi text-xl ${
                        expenditureFromOtherFund?.filename?.endsWith('.pdf')
                          ? 'pi-file-pdf text-red-500'
                          : 'pi-file-word'
                      }`}
                    />{' '}
                    <span className="text-blue-500 underline font-italic ml-1">
                      {formatFileNameProduct(
                        expenditureFromOtherFund?.filename || '',
                        projectCode,
                        'KP',
                        -1
                      )}
                    </span>
                  </span>
                )
              ) : (
                <span>
                  {formatFileNameProduct(
                    expenditureFromOtherFund?.file?.name || '',
                    projectCode,
                    'KP',
                    -1
                  )}
                </span>
              )}
            </td>
            {!disabled && (
              <td>
                <div className="flex justify-content-center my-2">
                  <i
                    onClick={() => {
                      allocatedExpenditureDialogRef.current.open({
                        control,
                        errors,
                        projectCode,
                        value: expenditureFromOtherFund,
                        update: (data) => {
                          if (!data) return;
                          setValue('summaryReportAllocatedExpenditureFromOtherFund', data, {
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                        },
                        type: 'UPDATE',
                      });
                    }}
                    className="edit-icon pi pi-pencil text-primary cursor-pointer mt-1"
                    data-pr-tooltip={t('formLayout.action.edit')}
                    data-pr-position="left"
                  />
                  <Tooltip target=".edit-icon" />
                </div>
              </td>
            )}
          </tr>
        </tbody>
      </table>

      <AllocatedExpenditureDialog
        ref={allocatedExpenditureDialogRef}
        handleDownloadFile={handleDownloadFile}
        productTypeShort="KP"
        isExpenditureFromOtherFund
      />
    </div>
  );
}

AllocatedExpenditureTable.propTypes = {
  disabled: PropTypes.bool,
  errors: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  setValue: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  expenditureFromOtherFund: PropTypes.object.isRequired,
  projectCode: PropTypes.string,
  setExpenditurePeriodsDelete: PropTypes.func,
  handleDownloadFile: PropTypes.func,
};

AllocatedExpenditureTable.defaultProps = {
  disabled: false,
  projectCode: '',
  setExpenditurePeriodsDelete: () => {},
  handleDownloadFile: () => {},
};
